import { useState } from "react";
import { useSessao } from "@/sessao/useSessao";
import { useConfigurarEntrega } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { Botao } from "@/componentes/Botao";
import { Campo } from "@/componentes/Campo";
import { Confirmacao } from "@/componentes/Confirmacao";
import { Dica } from "@/componentes/Dica";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";

/*
 * Configuração de entrega — change 20d, refeita pela `add-acoes-operacionais-do-painel`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ CAMPO VAZIO MANTÉM O SEGREDO, E A TELA NUNCA RECEBE A REFERÊNCIA.        │
 * │                                                                          │
 * │ A API só diz se o segredo existe (`segredo_configurado`). O `PUT` antigo │
 * │ trocava o destino INTEIRO: quem corrigia a URL mandava a referência      │
 * │ vazia sem saber, o segredo sumia, e cada evento novo era descartado sem  │
 * │ alerta. Agora o ausente preserva, e por isso a referência só entra no    │
 * │ corpo quando alguém a preenche. Apagar é o gesto de `RemoverSegredo`.    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Reconfigurar zera o estado degradado e o contador de falhas: a contagem era contra o
 * endereço antigo, e mantê-la puniria o novo pelo erro do anterior.
 */
export function ConfigurarEntrega({
  sistemaID,
  sistemaNome,
  urlAtual,
  segredoConfigurado,
  degradado,
  aoFechar,
}: {
  sistemaID: string;
  sistemaNome: string;
  urlAtual: string;
  segredoConfigurado: boolean;
  degradado: boolean;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const configurar = useConfigurarEntrega(cliente, sistemaID);

  const [webhook, setWebhook] = useState(urlAtual);
  const [segredoRef, setSegredoRef] = useState("");

  function enviar() {
    const referencia = segredoRef.trim();

    configurar.mutate(
      {
        webhook_url: webhook,
        ...(referencia !== "" && { webhook_secret_ref: referencia }),
      },
      {
        onSuccess: () => {
          avisos.feito("Entrega salva");
          aoFechar();
        },
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Configurar entrega"
      descricao={sistemaNome}
      aoEnviar={enviar}
      enviando={configurar.isPending}
      erro={configurar.error ? textoDoErro(configurar.error) : undefined}
      rotuloDeEnvio="Salvar entrega"
    >
      <Campo
        rotulo="Webhook"
        type="url"
        value={webhook}
        onChange={(evento) => setWebhook(evento.target.value)}
        placeholder="https://cliente.exemplo.com/webhook"
        autoFocus
        required
        dica="Endereço público: rede interna (127.0.0.1, 10.x, .cluster.local) é recusada."
      />

      <Campo
        rotulo="Referência do segredo"
        value={segredoRef}
        onChange={(evento) => setSegredoRef(evento.target.value)}
        placeholder="webhook/cliente-x"
        autoComplete="off"
        dica={
          segredoConfigurado
            ? "Vazio mantém o segredo atual. Para trocar, o nome no cofre, nunca o segredo."
            : "O nome no cofre, nunca o segredo. Sem ele, os eventos novos são descartados."
        }
      />

      {degradado && (
        <p className="flex items-center gap-2 rounded-controle border border-fio bg-superficie-2 px-3 py-2 text-corpo text-texto">
          Entrega degradada
          <Dica texto="Salvar zera o contador de falhas." />
        </p>
      )}
    </ModalDeCadastro>
  );
}

/*
 * Apagar o segredo é um gesto, e não um campo vazio: desliga a assinatura, e daí em diante
 * os eventos somem sem erro. Mora na aba, e não no formulário, para não levar junto a URL
 * que alguém digitou e não salvou. A rota exige `webhook_url`: vai a gravada.
 */
export function RemoverSegredo({
  sistemaID,
  sistemaNome,
  url,
}: {
  sistemaID: string;
  sistemaNome: string;
  url: string;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const configurar = useConfigurarEntrega(cliente, sistemaID);
  const [pedindo, setPedindo] = useState(false);

  return (
    <>
      <Botao tom="destrutivo" onClick={() => setPedindo(true)}>
        Remover segredo
      </Botao>

      <Confirmacao
        pedido={
          pedindo
            ? {
                severidade: "critica",
                titulo: `Remover o segredo de ${sistemaNome}?`,
                corpo: "Os eventos novos passam a ser descartados, sem alerta e sem retentativa.",
                verbo: "Remover segredo",
                digitarParaLiberar: sistemaNome,
              }
            : null
        }
        aoCancelar={() => setPedindo(false)}
        aoConfirmar={async () => {
          await configurar.mutateAsync({ webhook_url: url, remover_segredo: true });
          setPedindo(false);
          avisos.feito("Segredo removido");
        }}
      />
    </>
  );
}
